import { useEffect, useState } from 'react';
import { X, Megaphone } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

interface Announcement {
  id: string;
  title: string;
  body: string;
  variant: string;
}

const VARIANT_CLASSES: Record<string, string> = {
  info: 'bg-primary text-primary-foreground',
  success: 'bg-emerald-600 text-white',
  warning: 'bg-amber-500 text-black',
  critical: 'bg-destructive text-destructive-foreground',
};

export const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('announcements')
        .select('id, title, body, variant')
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(1);
      const item = data?.[0];
      if (!item) return;
      const dismissedId = localStorage.getItem('dismissed-announcement');
      if (dismissedId === item.id) { setDismissed(true); return; }
      setAnnouncement(item);
    })();
  }, []);

  if (!announcement || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem('dismissed-announcement', announcement.id);
    setDismissed(true);
  };

  return (
    <div className={`${VARIANT_CLASSES[announcement.variant] || VARIANT_CLASSES.info} fixed top-0 inset-x-0 z-[60] px-4 py-2 flex items-center justify-between gap-3 shadow-md`}>
      <div className="flex items-center gap-2 min-w-0">
        <Megaphone className="h-4 w-4 shrink-0" />
        <p className="text-sm truncate">
          <span className="font-semibold">{announcement.title}</span>
          <span className="mx-2 opacity-60">·</span>
          <span className="opacity-90">{announcement.body}</span>
        </p>
      </div>
      <Button variant="ghost" size="icon" onClick={dismiss} className="h-7 w-7 shrink-0 hover:bg-black/10">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
